import React, { useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import Header from '../../organisms/Header/Header';
import colors from '../../theme/color';
import HeroBg from '../../../assets/images/projectportfolio/herobg.webp';
import HeroLaptop from '../../../assets/images/projectportfolio/herolaptop.webp';
import HeroLaptopScreen from '../../../assets/images/projectportfolio/laptopheroscreen.webp';
import HeroArrow from '../../../assets/images/projectportfolio/heroarrow.svg';

const HeroSection = ({ startAnimation }: { startAnimation: boolean }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const screenContainerRef = useRef<HTMLDivElement>(null as any);
  const screenImgRef = useRef<HTMLImageElement>(null);

  const rawY = useMotionValue(0);

  const smoothY = useSpring(rawY, { stiffness: 50, damping: 22, mass: 0.4 });

  const getMaxScroll = (): number => {
    const container = screenContainerRef.current;
    const img = screenImgRef.current;
    if (!container || !img) return 0;
    const overflow = img.offsetHeight - container.offsetHeight;
    return overflow > 0 ? overflow : 0;
  };

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll = getMaxScroll();
      // Clamp: never go above 0 (top) or below -maxScroll (bottom)
      const clamped = -Math.min(Math.max(window.scrollY, 0), maxScroll);
      rawY.set(clamped);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [rawY]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full pt-28 md:pt-36 lg:pt-40 flex flex-col"
    >
    
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full z-0 overflow-hidden pointer-events-none rounded-b-[50px] md:rounded-b-[100px]">
        <img
          src={HeroBg}
          alt="Background"
          className="w-full h-full object-cover opacity-90"
        />
      </div>

      <div className="absolute top-0 w-full z-50">
        <Header textColor="text-black" />
      </div>
      <div className="relative z-10 w-full mx-auto px-4 flex flex-col items-center">

        <div className="text-center w-full z-10 mb-8 md:mb-10 lg:mb-12 mt-[2%]">

          <motion.h1
            className="text-xl font-gilroy-bold sm:text-xl md:text-3xl lg:text-24xl xl:text-5xl 2xl:text-[3rem] leading-tight"
            initial={{ x: -100, opacity: 0 }}
            whileInView={startAnimation ? { x: 0, opacity: 1 } : {}}
            viewport={{ once: false }}
            transition={{ duration: 1.2, ease: 'easeIn' }}
          >
            <span className="block text-black">From Discovery to Delivery:</span>
          </motion.h1>

          <motion.h1
            className="text-xl font-gilroy-bold sm:text-xl md:text-3xl lg:text-24xl xl:text-5xl 2xl:text-[3rem] leading-tight"
            initial={{ x: 100, opacity: 0 }}
            whileInView={startAnimation ? { x: 0, opacity: 1 } : {}}
            viewport={{ once: false }}
            transition={{ duration: 1.2, ease: 'easeIn', delay: 0.2 }}
          >
            <span className="block" style={{ color: colors.portfolioGreen }}>
              A Full-Stack E-Commerce Solution
            </span>
          </motion.h1>
        </div>
        <div className="w-full flex justify-center items-end relative z-10">
          <div className="relative w-[95%] sm:w-[90%] md:w-[75%] lg:w-[65%] xl:w-[50%] 2xl:w-[55%] max-w-[1900px]">
            <motion.div
              ref={screenContainerRef}
              className="absolute z-10 overflow-hidden "
              style={{
                left: '12.5%',
                top: '0%',
                width: '75%',
                height: '85.7%',
              }}
              initial={{ scale: 0.7, opacity: 0 }}
              whileInView={startAnimation ? { scale: 1, opacity: 1 } : {}}
              viewport={{ once: false }}
              transition={{ duration: 1.2, ease: 'easeIn' }}
            >
              <motion.img
                ref={screenImgRef}
                src={HeroLaptopScreen}
                alt="Hero laptop screen"
                className="w-full h-auto block"
                style={{ y: smoothY }}
                onLoad={() => rawY.set(-Math.min(Math.max(window.scrollY, 0), getMaxScroll()))}
              />
            </motion.div>

            <motion.img
              src={HeroLaptop}
              alt="Hero laptop"
              className="relative z-20 w-full h-auto object-cover object-bottom"
              initial={{ scale: 0.7, opacity: 0 }}
              whileInView={startAnimation ? { scale: 1, opacity: 1 } : {}}
              viewport={{ once: false }}
              transition={{ duration: 1.2, ease: 'easeIn' }}
            />

            {/* ARROW */}
            <motion.img
              src={HeroArrow}
              alt="Arrow"
              className="absolute rotate-5 top-4 right-0 md:right-7 lg:right-10 translate-x-1/3 -translate-y-1/3 w-[15%] sm:w-[15%] md:w-[12%] lg:w-[14%] object-contain z-20"
              animate={startAnimation ? { rotate: [0, 10, 0, -10, 0] } : { rotate: 0 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeIn' }}
            />

          </div>
        </div>

      </div>
    </section>
  );
};

export default HeroSection;